import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Image, Tag, Form, Input, Select, Button, Space, Card, Modal, Dropdown, MenuProps } from 'antd';
import { SearchOutlined, FilterOutlined, ReloadOutlined, EyeOutlined, DeleteOutlined, EditOutlined, EllipsisOutlined } from '@ant-design/icons';
import axios from '../../config/axios';
import AppLayout from '../../layouts/app.layout';
import { handleAPIError } from '../../utils/helpers';
import { User } from '../../types/user.types';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import CopyableText from '../../components/copyable-text';
import dayjs from '../../config/dayjs';
import { useContext, useState } from 'react';
import { UserDetails } from '../../components/user-details';
import { AuthContext } from '../../context/auth.context';
import EditUser from '../../components/edit-user';

interface FilterValues {
	role: string | null;
	status: string | null;
	search_term: null;
	phone_number: null;
}

const Users = () => {
	const { user } = useContext(AuthContext);
	const queryClient = useQueryClient();

	const [form] = Form.useForm<FilterValues>();
	const [paginationConfig, setPaginationConfig] = useState({
		page: 1,
		pageSize: 10,
	});
	const [count, setCount] = useState(0);
	const [filters, setFilters] = useState<FilterValues>({
		role: null,
		status: null,
		search_term: null,
		phone_number: null,
	});
	const [selectedUser, setSelectedUser] = useState<number | null>(null);
	const [editUserId, setEditUserId] = useState<number | null>(null);
	const [editUserData, setEditUserData] = useState<User | undefined>(undefined);

	const { data, error, isLoading } = useQuery({
		queryKey: ['users', paginationConfig.page, paginationConfig.pageSize, filters],
		queryFn: async () => {
			const response = await axios.get('/admin/users', {
				withCredentials: true,
				params: {
					page: paginationConfig.page,
					limit: paginationConfig.pageSize,
					...filters,
				},
			});
			setCount(response.data.count);
			return response.data;
		},
	});

	if (error) {
		handleAPIError(error);
	}

	const users: User[] = data?.results || [];

	const getStatusColor = (status: User['status']) => {
		switch (status) {
			case 'verified':
				return 'green';
			case 'pending verification':
				return 'blue';
			case 'incomplete':
				return 'gold';
			case 'blocked':
				return 'red';
			case 'temporary blocked':
				return 'volcano';
			default:
				return 'default';
		}
	};

	const handleDeleteUser = async (userId: number) => {
		try {
			await axios.delete(`/admin/users/${userId}`, {
				withCredentials: true,
			});
			// Refetch users after deletion
			queryClient.invalidateQueries({ queryKey: ['users'] });
		} catch (err) {
			const error = err as Error;
			handleAPIError(error);
		}
	};

	const showDeleteConfirm = (userId: number) => {
		Modal.confirm({
			title: 'Are you sure you want to delete this user?',
			content: 'This action cannot be undone.',
			okText: 'Yes, Delete',
			okType: 'danger',
			cancelText: 'Cancel',
			onOk() {
				return handleDeleteUser(userId);
			},
		});
	};

	const handleEditUser = (record: User) => {
		setEditUserId(record.id);
		setEditUserData(record);
	};

	const handleEditSuccess = () => {
		queryClient.invalidateQueries({ queryKey: ['users'] });
	};

	const columns: ColumnsType<User> = [
		{
			title: 'ID',
			dataIndex: 'id',
			key: 'id',
		},
		{
			title: 'DP',
			dataIndex: 'dp',
			key: 'dp',
			render: (_, record) => (
				<Image
					src={record.dp ?? '/images/avatar-placeholder.png'}
					width={50}
					height={60}
					className="rounded-lg object-cover"
				/>
			),
		},
		{
			title: 'Name',
			key: 'name',
			render: (_, record) => `${record.first_name.toUpperCase()} ${record.last_name.toUpperCase()}`,
		},
		{
			title: 'Phone Number',
			dataIndex: 'phone_number',
			key: 'phone_number',
			render: (_, record) => (
				<CopyableText text={record.phone_number}>
					<a href={`tel:+${record.phone_number}`}>{record.phone_number}</a>
				</CopyableText>
			),
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			render: (_, record) => <Tag color={getStatusColor(record.status)}>{record.status.toUpperCase()}</Tag>,
		},
		{
			title: 'Registration At',
			key: 'created_at',
			render: (_, record) => dayjs(record.created_at).format('DD-MM-YYYY LT'),
		},
		{
			title: 'Actions',
			key: 'id',
			render: (_, record) => {
				const items: MenuProps['items'] = [
					{
						key: 'view',
						label: 'View',
						icon: <EyeOutlined />,
						onClick: () => setSelectedUser(record.id),
					},
				];

				// Only add edit and delete options for superusers
				if (user?.is_superuser) {
					items.push(
						{
							key: 'edit',
							label: 'Edit',
							icon: <EditOutlined />,
							onClick: () => handleEditUser(record),
						},
						{
							key: 'delete',
							label: 'Delete',
							icon: <DeleteOutlined />,
							danger: true,
							onClick: () => showDeleteConfirm(record.id),
						},
					);
				}

				return (
					<Dropdown
						menu={{ items }}
						placement="bottomRight"
						arrow
						trigger={['click']}
					>
						<Button
							type="text"
							icon={<EllipsisOutlined style={{ fontSize: '20px' }} />}
						/>
					</Dropdown>
				);
			},
		},
	];

	const handleFilterApply = (values: FilterValues) => {
		setFilters(values);
		setPaginationConfig({
			...paginationConfig,
			page: 1, // Reset to first page when filtering
		});
	};

	const handleFilterReset = () => {
		form.resetFields();
		setFilters({
			role: null,
			status: null,
			search_term: null,
			phone_number: null,
		});
		setPaginationConfig({
			...paginationConfig,
			page: 1,
		});
	};

	const statusOptions = [
		{ value: 'verified', label: 'Verified' },
		{ value: 'pending verification', label: 'Pending Verification' },
		{ value: 'incomplete', label: 'Incomplete' },
		{ value: 'blocked', label: 'Blocked' },
		{ value: 'temporary blocked', label: 'Temporary Blocked' },
	];

	const roleOptions = [
		{ value: 'customer', label: 'Customer' },
		{ value: 'admin', label: 'Admin' },
	];

	return (
		<AppLayout
			title="Users"
			subtitle="Manage user accounts and permissions."
		>
			<Card className="mb-4 shadow-sm">
				<Form
					form={form}
					layout="horizontal"
					onFinish={handleFilterApply}
					initialValues={filters}
				>
					<div className="flex flex-wrap items-end gap-4">
						<Form.Item
							name="search_term"
							label="Search"
							className="mb-0 min-w-[200px] flex-1"
						>
							<Input
								placeholder="Search by name"
								prefix={<SearchOutlined />}
								allowClear
							/>
						</Form.Item>

						<Form.Item
							name="phone_number"
							label="Phone Number"
							className="mb-0 min-w-[180px]"
						>
							<Input
								placeholder="Enter phone number"
								allowClear
							/>
						</Form.Item>

						<Form.Item
							name="role"
							label="Role"
							className="mb-0 min-w-[150px]"
						>
							<Select
								placeholder="Select role"
								allowClear
								options={roleOptions}
							/>
						</Form.Item>

						<Form.Item
							name="status"
							label="Status"
							className="mb-0 min-w-[180px]"
						>
							<Select
								placeholder="Select status"
								allowClear
								options={statusOptions}
							/>
						</Form.Item>

						<Form.Item className="mb-0 flex-shrink-0">
							<Space>
								<Button
									type="primary"
									htmlType="submit"
									icon={<FilterOutlined />}
								>
									Apply Filters
								</Button>
								<Button
									onClick={handleFilterReset}
									icon={<ReloadOutlined />}
								>
									Reset
								</Button>
							</Space>
						</Form.Item>
					</div>
				</Form>
			</Card>

			<div className="mb-20">
				<Table
					columns={columns}
					dataSource={users}
					rowKey="id"
					pagination={{
						total: count,
						pageSize: paginationConfig.pageSize,
						current: paginationConfig.page,
						showSizeChanger: true,
						showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
						onChange: (page, pageSize) => {
							setPaginationConfig({ page, pageSize });
						},
					}}
					loading={isLoading}
				/>
			</div>

			{selectedUser && (
				<UserDetails
					id={selectedUser}
					onClose={() => setSelectedUser(null)}
				/>
			)}

			{editUserId && (
				<EditUser
					open={editUserId !== null}
					onClose={() => setEditUserId(null)}
					userId={editUserId}
					onSuccess={handleEditSuccess}
					userData={editUserData}
				/>
			)}
		</AppLayout>
	);
};

export default Users;
